/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import { useMinisite } from '@api/hooks';
import { SortableArrayField, ContentModuleModal, TextField, MinisiteField } from '@molecules';
import { Button } from '@atoms';
import { useModalControls } from '@hooks';

import Form from '../Form';

import Item from './Item';
import { INITIAL_VALUES, FORM_FIELD } from './constants';
import LocationFormModal from './LocationFormModal';
import LocationItem from './LocationItem';

const InteractiveMapModal = ({
  visible,
  hide,
  previewImg,
  onDelete,
  onSubmit,
  initialValues: data = INITIAL_VALUES,
  errors,
}) => {
  const [form] = Form.useForm(FORM_FIELD);
  const modalControls = useModalControls(false);
  const [editable, setEditable] = React.useState(null);

  const minisiteQuery = useMinisite({ id: data?.minisiteId });
  React.useEffect(() => {
    if (visible && minisiteQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.MINISITE.name]: minisiteQuery.data });
    }
  }, [form, minisiteQuery.data, minisiteQuery.status, visible]);

  const initialValues = React.useMemo(() => form.deserialize(data), [data, form]);

  const handleClose = () => {
    form.resetFields();
    hide();
  };

  const handleCloseLocationFormModal = () => {
    modalControls.hide();
    setEditable(null);
  };

  const handleSubmit = (values) => {
    onSubmit(values);
    handleClose();
  };

  const handleAddLocation = (location) => {
    const previousLocations = form.getFieldValue(FORM_FIELD.LOCATIONS.name);
    form.setFieldsValue({ [FORM_FIELD.LOCATIONS.name]: [...previousLocations, location] });
  };

  const handleEditLocation = (newLocation) => {
    const locations = form.getFieldValue(FORM_FIELD.LOCATIONS.name);
    const editableLocationIndex = locations.findIndex((location) => location === editable);
    if (editableLocationIndex >= 0) {
      form.setFieldsValue({
        [FORM_FIELD.LOCATIONS.name]: [
          ...locations.slice(0, editableLocationIndex),
          newLocation,
          ...locations.slice(editableLocationIndex + 1),
        ],
      });
    }
  };

  return (
    <ContentModuleModal
      visible={visible}
      title="Interactive map"
      subtitle="Add locations to appear as markers on a map"
      okText={onDelete ? 'SAVE CHANGES' : 'ADD INTERACTIVE MAP'}
      onCancel={handleClose}
      onSubmit={form.submit}
      onDelete={onDelete}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <Form
        form={form}
        initialValues={initialValues}
        onSerializedFinish={handleSubmit}
        layout="vertical"
        name="InteractiveMap"
      >
        <TextField {...FORM_FIELD.TITLE} />
        <MinisiteField
          {...FORM_FIELD.MINISITE}
          disabled={minisiteQuery.isFetching}
          extra={null}
          wrapperCol={{ span: 24 }}
        />
        <SortableArrayField {...FORM_FIELD.LOCATIONS} form={form} withPlaceholder withCount>
          {(location) => <LocationItem location={location} onClick={() => setEditable(location)} />}
        </SortableArrayField>
        <div style={{ textAlign: 'right', marginTop: 20 }}>
          <Button type="customBlue" onClick={modalControls.show}>
            ADD LOCATION
          </Button>
        </div>
      </Form>
      <LocationFormModal
        {...modalControls}
        visible={modalControls.visible || editable}
        initialValues={editable}
        hide={handleCloseLocationFormModal}
        onSubmit={editable ? handleEditLocation : handleAddLocation}
      />
    </ContentModuleModal>
  );
};

InteractiveMapModal.Item = Item;

export default InteractiveMapModal;
